/**
 * ХОД 3 — фоновое подключение CV-сессии (session pre-connect).
 * ВЕРСИЯ 2 (исправлена по результатам сверки с реальным кодом):
 *  1) колбэки передаются пробе через «трамплины» (sess.hooks.*), а НЕ перезаписью
 *     свойств клиента/протокола — работает при любом внутреннем устройстве
 *     CvWebRtcClient / CvProtocolHandler;
 *  2) проверка связи через реальное имя свойства this.pc (не peerConnection);
 *  3) поток отдаётся из s.warm — не зависим от client.stream.
 * Пока врач проходит предыдущий пункт шкалы, здесь идёт offer → ready →
 * калибровка. ArticulationGame при монтировании забирает готовую сессию.
 * ЖЕЛЕЗНОЕ ПРАВИЛО СТО: offer — только после открытой камеры (await prewarmCamera).
 * Ошибки наружу не летят: любой сбой = тихий откат к обычному пути.
 */
import { CvWebRtcClient } from "./CvWebRtcClient";
import { CvProtocolHandler } from "./CvProtocolHandler";
import { prewarmCamera, takeWarmStream } from "./cameraPrewarm";
import { calibrationFields } from "./cvClientMode";

export const CV_SESSION_PRECONNECT = true; // выключатель всего хода

const SESSION_TTL_MS = 240_000;        // жизнь сессии с момента старта
const CONNECT_MAX_MS = 30_000;         // камера + offer + рукопожатие
const CALIBRATION_MAX_MS = 40_000;     // после connect нет ready — зависла (кейс прогона 3)
const CALIBRATION_SILENCE_MS = 15_000; // тишина во время калибровки
const DISCONNECT_GRACE_MS = 5_000;
// после этих причин камера снова греется — для обычного пути пробы
const REWARM_REASONS = new Set([
  "connect_failed", "connect_timeout", "calib_hang", "silence",
  "pc_failed", "pc_disconnected", "dc_closed",
]);

let current = null;       // единственная фоновая сессия
let pendingTarget = null; // { exerciseConfig, userId } — следующая CV-проба шкалы
let seq = 0;
const preloaded = new Set();

const REVIVE_REASONS = new Set(["calib_hang", "silence", "calib_stuck", "pc_failed", "pc_disconnected", "dc_closed", "ttl"]);
const MAX_REVIVES = 2;          // лимит пересозданий на одну цель — чтобы не долбить сервер
const SLOW_CONNECT_MS = 3000;
const CALIB_TOTAL = 240;        // по логам счётчик Calibrating_* идёт до 240 (только для вывода)
const CALIB_STUCK_MS = 15_000;   // счётчик Calibrating_* не растёт — серверный сбой «ноль на месте»
const revives = new Map();
let stopped = false;            // шкала закрыта: реварм запрещён

const keyOf = (slug, userId) => `${slug}|${userId ?? ""}`;

function mark(s, name) { s.marks.push([name, performance.now()]); }
function logTimeline(s, extra) {
  const line = s.marks.map(([n, t]) => `${n} ${Math.round(t - s.t0)}ms`).join(" → ");
  const ph = s.phaseInfo ? " | " + s.phaseInfo : "";
  console.log(`[AG][CV-TIME] pre-sess#${s.id} ${s.slug}: ${line}${ph}${extra ? " | " + extra : ""}`);
}

// Дословная копия payload клинической пробы из ArticulationGame.handleStartCamera
// при cvInputs=null — сверено с реальным кодом поле-в-поле (report_mode,
// max_reps:1, personalization:false, mask_info, interval_grade [3,5] и т.д.).
function buildClinicalPayload(cfg, userId) {
  return {
    report_mode: "client",
    user_settings: { [cfg.slug]: { max_reps: 1 } },
    ...calibrationFields(null),
    personalization: false,
    user_id: userId ?? null,
    exercise_id: cfg.neuralNetId,
    db_exercise_id: cfg.dbId,
    exercise_slug: cfg.slug,
    mask_info: cfg.maskInfo || { use_mask: 0, key_mask: "none" },
    lang_id: 1,
    group: null,
    interval_grade: [3, 5],
    current_time: 0,
    max_time: 1800,
    list_words: {},
    video_transform: cfg.videoTransform || "none",
    cvUrl: cfg.cvUrl,
  };
}

function kill(s, reason) {
  if (s.status === "dead") return;
  s.status = "dead";
  s.hooks = null; // убираем трамплины: пробе больше ничего не приходит
  clearTimeout(s.ttlTimer);
  clearInterval(s.watch);
  clearTimeout(s.discTimer);
  try { s.protocol?.close(); } catch (e) { /* ignore */ }
  try { s.client?.close(); } catch (e) { /* ignore */ }
  try { s.warm?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
  if (current === s) current = null;
  mark(s, `pre:sess:end(${reason})`);
  logTimeline(s);
}

function revive(s, reason) {
  if (stopped || s.handedOver) return;
  const n = revives.get(s.key) || 0;
  if (n >= MAX_REVIVES) {
    console.warn(`[AG][CV-PREWARM] реварм (${reason}): лимит ${MAX_REVIVES} исчерпан, дальше обычный путь`);
    return;
  }
  revives.set(s.key, n + 1);
  console.warn(`[AG][CV-PREWARM] реварм ${n + 1}/${MAX_REVIVES} (${reason})`);
  setTimeout(() => { if (!stopped && !current) prewarmSession(s.cfg, s.userId); }, 1000);
}

function fail(s, reason, err) {
  if (s.status === "dead") return;
  console.warn(`[AG] pre-sess#${s.id} FAIL ${reason} (кадр ${s.calibFrame ?? "-"}/${CALIB_TOTAL})`, err ? String(err.message || err) : "");
  kill(s, reason);
  if (REWARM_REASONS.has(reason)) { try { prewarmCamera(); } catch (e) { /* ignore */ } }
  if (REVIVE_REASONS.has(reason)) revive(s, reason);
}

/* Трамплины: пока сессия не отдана пробе — работают стражи сессии;
   как только проба выставила sess.hooks.* — все сообщения идут ей. */
function onMsg(s, data) {
  s.lastMsgAt = Date.now();
  if (s.hooks && s.hooks.onMessage) { s.hooks.onMessage(data); return; }
  if (s.handedOver || s.status === "dead") return;
  if (data.kp) return;
  for (const k of Object.keys(data)) {
    if (k.startsWith("Calibrating_")) {
      const v = Number(data[k]);
      if (v !== s.calibFrame) { s.calibFrame = v; s.calibFrameAt = Date.now(); }
      break;
    }
  }
    if (data.calibrator !== undefined && !s.calibMarked && String(data.calibrator) === "1") {
    s.calibMarked = true;
    s.calibratedAt = Date.now();
    mark(s, "pre:sess:calibrated");
  }
  if (data.start_button_activate && s.status !== "ready") {
    s.status = "ready";
    s.readyMsg = data; // проба получит его при взятии и сама запустит озвучку + START
    mark(s, "pre:sess:ready");
    logTimeline(s);
  }
}

function onPcState(s, state) {
  if (s.hooks && s.hooks.pcState) { s.hooks.pcState(state); return; }
  if (s.handedOver || s.status === "dead") return;
  if (state === "connected") {
    clearTimeout(s.discTimer);
    if (!s.pcMarked) { s.pcMarked = true; mark(s, "pre:sess:pc"); }
  } else if (state === "disconnected") {
    clearTimeout(s.discTimer);
    s.discTimer = setTimeout(() => fail(s, "pc_disconnected"), DISCONNECT_GRACE_MS);
  } else if (state === "failed" || state === "closed") {
    fail(s, "pc_failed");
  }
}

function watchTick(s) {
  if (s.handedOver || s.status === "dead") return;
  const now = Date.now();
  if (s.status === "starting" && now - s.startedAt > CONNECT_MAX_MS) return fail(s, "connect_timeout");
  if (s.status === "calibrating") {
      if (!s.calibMarked) {
      // 40-секундный бюджет — ТОЛЬКО на саму калибровку (до calibrator:'1')
      if (now - s.connectedAt > CALIBRATION_MAX_MS) return fail(s, "calib_hang");
    }
    // После calibrator:'1' ждём start_button сколько нужно: сервер, судя по всему,
    // шлёт кнопку только при стабильном лице, а врач ещё на предыдущих пунктах.
    if (now - s.lastMsgAt > CALIBRATION_SILENCE_MS) return fail(s, "silence");
  }
}

async function connectSession(s, cfg, userId) {
  // ЖЕЛЕЗНОЕ ПРАВИЛО: сначала камера, потом offer.
  await prewarmCamera();
  if (s.status === "dead") return;
  const warm = takeWarmStream();
  if (!warm) throw new Error("no_camera");
  s.warm = warm;
  s.hooks = {}; // трамплины появляются только у живой сессии с потоком
  mark(s, "pre:sess:camera");

  const client = new CvWebRtcClient({
    iceServers: cfg.iceServers ?? [],
    onConnectionStateChange: (st) => onPcState(s, st),
  });
  s.client = client;
  await client.start({ warmStream: warm }); // с warmStream — без нового getUserMedia
  if (s.status === "dead") { client.close(); return; }

  s.protocol = new CvProtocolHandler({
    client,
    onMessage: (d) => onMsg(s, d),
    onClose: () => {
      if (s.hooks && s.hooks.onClose) { s.hooks.onClose(); return; }
      if (!s.handedOver) fail(s, "dc_closed");
    },
    onAlive: () => { s.lastMsgAt = Date.now(); if (s.hooks?.onAlive) s.hooks.onAlive(); },
    onReport: (r) => { if (s.hooks?.onReport) s.hooks.onReport(r); },
  });
  const tConn = performance.now();
  await s.protocol.connect(buildClinicalPayload(cfg, userId));
  const connMs = Math.round(performance.now() - tConn);
  const tm = s.protocol.timing || {};
  s.phaseInfo = `connect ${connMs}ms (ice ${tm.iceMs ?? "?"} / http ${tm.httpMs ?? "?"} / sdp ${tm.sdpMs ?? "?"})`;
  if (connMs > SLOW_CONNECT_MS) console.warn(`[AG][CV-PREWARM] медленный коннект: ${connMs}мс (${s.phaseInfo})`);
  if (s.status === "dead") return;
  if (s.status === "starting") s.status = "calibrating"; // ready мог прийти раньше
  s.connectedAt = Date.now();
  s.lastMsgAt = Date.now();
  mark(s, "pre:sess:connected");
}

async function startSession(cfg, userId, key) {
  const s = {
    id: ++seq, key, slug: cfg.slug, cfg, userId, status: "starting",
    calibFrame: null, calibMarked: false, phaseInfo: "",
    t0: performance.now(), startedAt: Date.now(), marks: [],
    client: null, protocol: null, warm: null, readyMsg: null, hooks: null,
    connectedAt: 0, lastMsgAt: Date.now(),
    handedOver: false, taken: false, pcMarked: false,
  };
  current = s;
  mark(s, "pre:sess:start");
  console.log(`[AG] pre-sess#${s.id} START ${s.slug}`);
  s.ttlTimer = setTimeout(() => {
    if (s.handedOver || s.status === "dead") return;
    console.warn(`[AG][CV-PREWARM] TTL ${SESSION_TTL_MS}мс истёк (state=${s.status})`);
    kill(s, "ttl");
    revive(s, "ttl");
  }, SESSION_TTL_MS);
  s.watch = setInterval(() => watchTick(s), 1000);
  s.connectPromise = connectSession(s, cfg, userId);
  try { await s.connectPromise; } catch (err) { fail(s, "connect_failed", err); }
}

/** Запустить фоновую сессию. Идемпотентна, никогда не бросает. */
export function prewarmSession(exerciseConfig, userId = null) {
  if (!CV_SESSION_PRECONNECT) return;
  try {
    if (!exerciseConfig?.slug) return;
    stopped = false;
    const key = keyOf(exerciseConfig.slug, userId);
    if (current && current.status !== "dead") {
      if (current.key === key) return;
      kill(current, "other_target");
    }
    startSession(exerciseConfig, userId, key).catch(() => {});
  } catch (e) { console.warn("[AG] prewarmSession:", e); }
}

/** Следующая CV-проба шкалы (ScaleRunner обновляет при каждом шаге). */
export function setNextCvTarget(exerciseConfig, userId = null) {
  pendingTarget = exerciseConfig?.slug ? { exerciseConfig, userId } : null;
}
export function clearNextCvTarget() { pendingTarget = null; }

/** Вызывать из пробы после closeConnection: окно ~2,5 с до следующей CV-пробы. */
export function prewarmPendingSession() {
  if (pendingTarget) prewarmSession(pendingTarget.exerciseConfig, pendingTarget.userId);
}

/**
 * Забрать сессию (ровно один раз). null → идти обычным путём.
 * Если сессия ещё подключается — ждём её, а не открываем вторую.
 */
export async function takeSession(slug, userId = null) {
  if (!CV_SESSION_PRECONNECT) return null;
  try {
    const s = current;
    if (!s || s.status === "dead") return null;
    if (s.key !== keyOf(slug, userId)) { kill(s, "key_mismatch"); return null; } // без двойных подключений
    current = null;
    s.taken = true;
    if (s.status === "starting") {
      try { await s.connectPromise; } catch (e) { fail(s, "connect_failed", e); }
    }
    const pcState = s.client?.pc?.connectionState; // у клиента соединение хранится в this.pc
    if (s.status === "dead" || !s.protocol || !s.client || !s.warm || pcState === "failed" || pcState === "closed") {
      kill(s, "not_usable");
      return null;
    }
    s.handedOver = true;
    clearTimeout(s.ttlTimer);
    clearInterval(s.watch);
    clearTimeout(s.discTimer);
    const ready = s.status === "ready";
    revives.delete(s.key);
    mark(s, "pre:sess:taken");
    const pcT = s.marks.find((m) => m[0] === "pre:sess:pc");
    const sessMs = pcT ? Math.round(performance.now() - pcT[1]) : "?";
    logTimeline(s, `TAKEN state=${ready ? "ready" : "calibrating" + (s.calibFrame != null ? ` кадр ${s.calibFrame}/${CALIB_TOTAL}` : "")} | sess(pc→taken) ${sessMs}ms`);
    return {
      client: s.client,
      protocol: s.protocol,
      stream: s.warm,
      hooks: s.hooks, // проба заполняет: pcState, onMessage, onClose, onAlive, onReport
      ready,
      readyMsg: ready ? s.readyMsg : null,
      calibFrame: s.calibFrame,
      calibrated: s.calibMarked,
      sinceMilestoneMs: Date.now() - (s.calibMarked ? s.calibratedAt : s.connectedAt),
      calibLeftMs: Math.max(1000, CALIBRATION_MAX_MS - (Date.now() - (s.connectedAt || Date.now()))),
      release: () => kill(s, "released_by_component"),
    };
  } catch (e) {
    console.warn("[AG] takeSession:", e);
    return null;
  }
}

/** Шкала закрыта — гасим всё. */
export function releaseSession() {
  pendingTarget = null;
  stopped = true;
  revives.clear();
  if (current) kill(current, "released");
}

/** Приоритет №3: заранее кладём mp3 инструкции в HTTP-кеш. */
export function preloadAudio(url) {
  try {
    if (!url || preloaded.has(url)) return;
    preloaded.add(url);
    const a = new Audio();
    a.preload = "auto";
    a.src = url;
  } catch (e) { /* ignore */ }
}
